/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useRouteLoaderData } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NavLink } from '@/components/link';
import { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import type { ShopperProducts } from '@/scapi';
import { routes, routeHref } from '@/route-paths';
import { cn } from '@/lib/utils';

type Category = ShopperProducts.schemas['Category'];

/** Shape of the `_app` layout loader data this nav reads (see `src/routes/_app.tsx`). */
type AppNavData = { root?: Promise<Category>; subs?: Promise<Category[]> };

interface MainNavProps {
    orientation?: 'horizontal' | 'vertical';
    onNavigate?: () => void;
    className?: string;
}

/**
 * Nav ordering, mirroring SFRA's `sortOrder`: items are sorted ascending by the merchandiser-set
 * **Sort Order** (`c_sortOrder`); items without one sort to the end and keep their catalog order
 * (`Array.prototype.sort` is stable).
 */
function byNavOrder(a: Category, b: Category): number {
    const order = (c: Category) => (typeof c.c_sortOrder === 'number' ? c.c_sortOrder : Number.MAX_SAFE_INTEGER);
    return order(a) - order(b);
}

/** A nav item's destination, resolved from the category's merchandiser-set attributes. */
type NavTarget = { href: string; external: boolean };

/**
 * Destination for a nav category, mirroring SFRA's `getCategoryUrl`:
 * - **Alternative URL** (`c_alternativeUrl`) when set — for non-PLP targets like `/learn`, landing
 *   pages, or external links. An absolute `http(s)://` value is treated as external (plain `<a>`, no
 *   site/locale prefixing); a relative value goes through the site-context-aware `NavLink`.
 * - otherwise the category's product-listing page.
 */
function navTarget(category: Category): NavTarget {
    const alt = category.c_alternativeUrl;
    if (typeof alt === 'string' && alt.trim()) {
        const href = alt.trim();
        return { href, external: /^https?:\/\//i.test(href) };
    }
    return { href: routeHref(routes.category, { categoryId: category.id }), external: false };
}

/**
 * The storefront's primary navigation — **merchandiser-managed via the storefront catalog**, the SF
 * Next equivalent of SFRA's category-driven menu. Items come from the navigation root category's
 * children (configured by `pages.navigation.rootCategoryId`); {@link WithCategoryNavigationMenu}
 * filters to categories with **Show in Menu** (`c_showInMenu`) checked. Each item links to its
 * Alternative URL (`c_alternativeUrl`) or its PLP, and **Disable Link** (`c_disableLink`) renders it
 * as a plain label. Shared by the desktop header (horizontal) and the mobile flyout (vertical).
 */
export default function MainNav({ orientation = 'horizontal', onNavigate, className }: MainNavProps): ReactElement {
    const { t } = useTranslation('header');
    const appData: AppNavData | undefined = useRouteLoaderData('routes/_app');
    const isVertical = orientation === 'vertical';

    // Acumin Pro Wide, uppercase, tight tracking. Vertical (mobile flyout) matches the Figma Mobile
    // H2 — 28px, Black, line-height 0.9; horizontal (desktop header) stays 22px, ExtraBold, 0.85.
    const typeClass = cn(
        'font-display uppercase tracking-[-0.05em]',
        isVertical ? 'block py-3 text-[28px] font-black leading-[0.9]' : 'text-[1.375rem] font-extrabold leading-[0.85]'
    );

    // Interactive hover. Vertical keeps a simple opacity fade. Desktop (horizontal) matches the Figma
    // NavBarText hover (11957:822): a soil-green background wipes in from the left (scaleX from the left
    // edge), then the label tilts -4° — both ~150ms, the tilt lagging the wipe so it reads as
    // "fill, then tilt". The label sits above a -z pseudo-element that paints the green.
    const linkClass = isVertical
        ? cn(typeClass, 'transition-opacity hover:opacity-70')
        : cn(
              typeClass,
              'group relative isolate inline-flex items-center px-0.5 py-1 text-header-foreground transition-colors duration-150 hover:text-bm-ink',
              'before:absolute before:inset-0 before:-z-10 before:origin-left before:scale-x-0 before:bg-bm-green before:transition-transform before:duration-150 before:ease-out hover:before:scale-x-100'
          );

    // Horizontal labels tilt on hover; the delay makes the tilt trail the background wipe.
    const renderLabel = (labelText: string) =>
        isVertical ? (
            labelText
        ) : (
            <span className="inline-block transition-transform delay-75 duration-150 group-hover:-rotate-[4deg]">
                {labelText}
            </span>
        );

    return (
        <nav aria-label={t('mainNavigation', 'Main navigation')} className={className}>
            <WithCategoryNavigationMenu resolve={appData?.root} defer={appData?.subs} fallback={null}>
                {({ categories }: { categories: Category[] }) => (
                    <ul className={cn('flex', isVertical ? 'flex-col' : 'flex-row items-center gap-x-8')}>
                        {[...categories].sort(byNavOrder).map((category) => {
                            const label = category.name ?? category.id;
                            const disabled = Boolean(category.c_disableLink);
                            const { href, external } = navTarget(category);
                            return (
                                <li key={category.id}>
                                    {disabled ? (
                                        <span
                                            className={cn(
                                                typeClass,
                                                'cursor-default',
                                                !isVertical && 'px-0.5 py-1 text-header-foreground'
                                            )}>
                                            {label}
                                        </span>
                                    ) : external ? (
                                        <a
                                            href={href}
                                            onClick={onNavigate}
                                            className={linkClass}
                                            rel="noopener noreferrer">
                                            {renderLabel(label)}
                                        </a>
                                    ) : (
                                        <NavLink to={href} onClick={onNavigate} className={linkClass}>
                                            {renderLabel(label)}
                                        </NavLink>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </WithCategoryNavigationMenu>
        </nav>
    );
}
